import { useContext, createContext, useState, useEffect } from 'react';
import axios from 'axios';
import api from '../utils/api';

// Create a context for authentication
const AuthContext = createContext(null);

// Provider component that wraps the app and makes auth object available to any child component that calls useAuth()
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [originalUser, setOriginalUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load full user data from API
  const loadUserData = async (userId) => {
    try {
      const response = await api.get(`/users/${userId}`);
      return response.data;
    } catch (err) {
      console.error('Error loading user data:', err);
      return null;
    }
  };

  // Get all active users for admin switching
  const getActiveUsers = async () => {
    try {
      const response = await api.get('/users?status=active');
      return response.data;
    } catch (err) {
      console.error('Error loading active users:', err);
      return [];
    }
  };

  // Switch to another user (admin only)
  const switchToUser = async (targetUserId) => {
    if (!user || user.role !== 'admin') {
      throw new Error('Only admins can switch users');
    }

    try {
      setIsLoading(true);

      // Call backend API to validate switch
      const response = await api.post(`/auth/switch-user/${targetUserId}`);

      if (response.data.user) {
        // Store original user if not already stored
        if (!originalUser) {
          setOriginalUser(user);
          localStorage.setItem('originalUser', JSON.stringify(user));
        }

        // Set switched user
        setUser(response.data.user);
        localStorage.setItem('switchedUser', JSON.stringify(response.data.user));

        return response.data.user;
      }

      throw new Error('Failed to switch user');
    } catch (err) {
      console.error('Error switching user:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Return to original admin user
  const returnToOriginalUser = () => {
    if (originalUser) {
      setUser(originalUser);
      setOriginalUser(null);
      localStorage.removeItem('originalUser');
      localStorage.removeItem('switchedUser');
    }
  };

  // Check if currently switched to another user
  const isSwitchedUser = () => {
    return !!originalUser;
  };

  // Check if user is already logged in on mount
  useEffect(() => {
    const checkAuth = async () => {
      let token;
      try {
        token = localStorage.getItem('accessToken');
      } catch (e) {
        setIsLoading(false);
        return;
      }
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          setIsLoading(false);
          return;
        }

        const response = await api.post('/auth/refresh-token', {
          refreshToken
        });

        localStorage.setItem('accessToken', response.data.accessToken);
        localStorage.setItem('refreshToken', response.data.refreshToken);

        const switchedUser = localStorage.getItem('switchedUser');
        const originalUserData = localStorage.getItem('originalUser');

        if (switchedUser && originalUserData) {
          try {
            setUser(JSON.parse(switchedUser));
            setOriginalUser(JSON.parse(originalUserData));
          } catch {
            localStorage.removeItem('switchedUser');
            localStorage.removeItem('originalUser');
          }
        } else {
          let userData;
          try {
            const payload = token.split('.')[1];
            if (!payload) throw new Error('Invalid token');
            userData = JSON.parse(atob(payload));
          } catch {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            setIsLoading(false);
            return;
          }

          const fullUserData = await loadUserData(userData.userId);
          if (fullUserData) {
            setUser(fullUserData);
          } else {
            setUser({
              id: userData.userId,
              email: userData.email,
              role: userData.role
            });
          }
        }
      } catch (err) {
        console.error('Authentication error:', err);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('originalUser');
        localStorage.removeItem('switchedUser');
        setError('Session expired. Please login again.');
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Login function
  const login = async (email, password) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.post('/auth/login', {
        email,
        password
      });

      // Store tokens
      localStorage.setItem('accessToken', response.data.accessToken);
      localStorage.setItem('refreshToken', response.data.refreshToken);

      // Load full user data
      const fullUserData = await loadUserData(response.data.userId);

      if (fullUserData) {
        setUser(fullUserData);
      } else {
        // Fallback to response data
        setUser({
          id: response.data.userId,
          name: response.data.name,
          email: response.data.email,
          role: response.data.role
        });
      }

      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please try again.');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Register function
  const register = async (name, email, password) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.post('/auth/register', {
        name,
        email,
        password
      });

      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Update user function
  const updateUser = async (updatedUserData) => {
    try {
      // If full user object is passed, use it directly
      if (updatedUserData && typeof updatedUserData === 'object') {
        setUser(prevUser => ({
          ...prevUser,
          ...updatedUserData
        }));

        // Update switched user data if currently switched
        if (isSwitchedUser()) {
          localStorage.setItem('switchedUser', JSON.stringify({
            ...user,
            ...updatedUserData
          }));
        }

        return { ...user, ...updatedUserData };
      }

      // If only user ID is passed, reload from API
      if (updatedUserData && (typeof updatedUserData === 'string' || typeof updatedUserData === 'number')) {
        const fullUserData = await loadUserData(updatedUserData);
        if (fullUserData) {
          setUser(fullUserData);

          // Update switched user data if currently switched
          if (isSwitchedUser()) {
            localStorage.setItem('switchedUser', JSON.stringify(fullUserData));
          }

          return fullUserData;
        }
      }

      return user;
    } catch (err) {
      console.error('Error updating user:', err);
      return user;
    }
  };

  // Logout function
  const logout = async () => {
    setIsLoading(true);

    try {
      // Call logout API
      await api.post('/auth/logout', {
        refreshToken: localStorage.getItem('refreshToken')
      });
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      // Clear tokens and user
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userData');
      localStorage.removeItem('originalUser');
      localStorage.removeItem('switchedUser');
      setUser(null);
      setOriginalUser(null);
      setIsLoading(false);
    }
  };

  // Forgot password function
  const forgotPassword = async (email) => {
    setIsLoading(true);
    setError(null);

    try {
      await api.post('/auth/forgot-password', { email });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send reset email. Please try again.');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Reset password function
  const resetPassword = async (token, password) => {
    setIsLoading(true);
    setError(null);

    try {
      await api.post(`/auth/reset-password/${token}`, { password });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Verify email function
  const verifyEmail = async (token) => {
    setIsLoading(true);
    setError(null);

    try {
      await api.get(`/auth/verify-email/${token}`);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to verify email. Please try again.');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Value object that will be passed to consumers
  const value = {
    user,
    originalUser,
    setUser,
    updateUser,
    isAuthenticated: !!user,
    isLoading,
    error,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    verifyEmail,
    // New functions for user switching
    getActiveUsers,
    switchToUser,
    returnToOriginalUser,
    isSwitchedUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Hook that lets any component access the auth context
export function useAuth() {
  return useContext(AuthContext);
}